import {
    PayrollReportState,
    PayrollReportDates,
} from "@/employer/types";
import * as UserService from "@/shared/api/userService";
import * as PayrollService from "@/shared/api/payrollService";
import moment from "moment";
import { FetchState } from "@/shared/types";
import { downloadCsvFile } from "@/shared/fileDownload";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useUserStore } from "@/shared/store/user";

export const initialState: PayrollReportState = {
    fetchState: FetchState.Uninitialized,
    payrollDates: {
        nextPayrollCutOffDate: null,
        reportDates: [],
    },
    automatedFundingLeaveDate: null,
    automatedFundingLeaveDateFetchState: FetchState.Uninitialized
};

export const usePayrollReportStore = defineStore("payrolLReport", {
    state: (): PayrollReportState => (_.cloneDeep(initialState)),
    getters: {
        nextPayrollCutoffDate: state => state.payrollDates.nextPayrollCutOffDate,
        payrollReportDates: state => state.payrollDates.reportDates,
        fetchLoadState: state => state.fetchState,
    },
    actions: {
        setFetchState (newState: FetchState): void {
            this.fetchState = newState;
        },
        setReportDates (payrollReportResult: PayrollReportDates): void {
            for (const reportDate of payrollReportResult.reportDates) {
                reportDate.payDate = moment.utc(reportDate.payDate).format("YYYY-MM-DD");
            }
            this.payrollDates = payrollReportResult;
        },
        setAutomatedFundingLeaveDateFetchState (newState: FetchState): void {
            this.automatedFundingLeaveDateFetchState = newState;
        },
        setAutomatedFundingLeaveDate (date: string): void {
            this.automatedFundingLeaveDate = date;
        },
        getPayrollReportDates (
            args: { employerId: number, automatedLeaveDate: string }): Promise<any> {
            this.setFetchState(FetchState.Loading);
            return new Promise<void>((resolve, reject) => {
                PayrollService.getPayrollReportDates(args.employerId, args.automatedLeaveDate).then(response => {
                    const stateData = response.data;
                    this.setReportDates(stateData);
                    this.setFetchState(FetchState.Loaded);
                    resolve();
                }).catch(err => {
                    this.setFetchState(FetchState.Uninitialized);
                    storeHelper.handleError({ error: err, rethrow: false });
                    reject(err);
                });
            });
        },
        getAutomatedFundingOptionLeaveDate (employerId: number): Promise<any> {
            this.setAutomatedFundingLeaveDateFetchState(FetchState.Loading);
            return new Promise<void>((resolve, reject) => {
                UserService.getAutomatedFundingOptionLeaveDate(employerId).then(response => {
                    this.setAutomatedFundingLeaveDate(response.data);
                    this.setAutomatedFundingLeaveDateFetchState(FetchState.Loaded);
                    resolve();
                }).catch(err => {
                    this.setAutomatedFundingLeaveDateFetchState(FetchState.Uninitialized);
                    storeHelper.handleError({ error: err, rethrow: false });
                    reject(err);
                });
            });
        },
        processPayrollChangeReport (
            request: { employerId: number, payrollDate: string }): Promise<any> {
            return PayrollService.downloadPayrollChangeReport(request.employerId, request.payrollDate)
                .then(response => {
                    const reportData = response.data;
                    const fileName = "payroll-change-report-" + moment(request.payrollDate).format("YYYY-MM-DD") +
                        ".csv";
                    downloadCsvFile(reportData, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        processPayrollReport (
            request: { employerId: number, payrollDate: string }): Promise<any> {
            return PayrollService.downloadPayrollFullReport(request.employerId, request.payrollDate)
                .then(response => {
                    const reportData = response.data;
                    const fileName = "payroll-report-" + moment(request.payrollDate).format("YYYY-MM-DD") + ".csv";
                    downloadCsvFile(reportData, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        generateEmployeeBankInfoReport (): Promise<any> {
            const userStore = useUserStore();
            return UserService.downloadEmployeeBankInfoReport(userStore.employerId)
                .then(response => {
                    const reportData = response.data;
                    const fileName = "employee-bankinfo-report-" + moment().format("YYYY-MM-DD") + ".csv";
                    downloadCsvFile(reportData, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        }
    }
});
